import { BigNumber } from 'bignumber.js';

import { addUniqueCustodyAddresses } from './addUniqueCustodyAddresses';
import { addUniqueSources } from './addUniqueSources';
import { addUniqueWalletIds } from './addUniqueWalletIds';

import type { AccountAddress } from '../../../types';
import type {
  ChainNftAssetBalance,
  ChainPositionChainAssetBalance,
  ChainTokenAssetBalance,
  NftBalancesByGroupId,
  PositionBalancesByGroupId,
  TokenBalancesByGroupId,
} from '../balance';

/**
 * Type mapping for grouping balances by group ID
 */
type GroupedBalancesMap<
  T extends (
    | ChainPositionChainAssetBalance
    | ChainTokenAssetBalance
    | ChainNftAssetBalance
  )[],
> = T extends ChainPositionChainAssetBalance[]
  ? PositionBalancesByGroupId
  : T extends ChainTokenAssetBalance[]
    ? TokenBalancesByGroupId
    : T extends ChainNftAssetBalance[]
      ? NftBalancesByGroupId
      : never;

/**
 * Generic function that groups balances by group ID
 *
 * Preserves the input type - if you pass in position balances, you get back
 * position balances grouped by group ID.
 */
export const groupBalancesByGroupId = <
  T extends (
    | ChainPositionChainAssetBalance
    | ChainTokenAssetBalance
    | ChainNftAssetBalance
  )[],
>(
  balances: T,
  accountAddresses: AccountAddress[]
): GroupedBalancesMap<T> => {
  const balancesByGroupId = {} as GroupedBalancesMap<T>;
  for (const balance of balances) {
    const groupId = balance.assetId ?? balance.id;

    let groupedBalance = balancesByGroupId[groupId];
    if (!groupedBalance) {
      const {
        walletAddress: _walletAddress,
        chain: _chain,
        id: _id,
        isRateLoading,
        rateUsd,
        rateUsdChange24h,
        ...rest
      } = balance;

      groupedBalance = {
        ...rest,
        id: groupId,
        // Group-level flag — identical across members; stamp explicitly so the
        // typed field does not rely on the first-member spread alone.
        isOfficial: balance.isOfficial,
        decimalTotalBalance: new BigNumber(0),
        chainAssetBalances: {},
        custody: [],
        walletIds: [],
        sources: [],
        ...(isRateLoading
          ? {
              isRateLoading,
              rateUsd: undefined,
              rateUsdChange24h: undefined,
              totalBalanceUsd: undefined,
              totalBalance24hChangeUsd: undefined,
            }
          : {
              isRateLoading: false,
              rateUsd,
              rateUsdChange24h,
              totalBalanceUsd: 0,
              totalBalance24hChangeUsd: 0,
            }),
      };

      balancesByGroupId[groupId] = groupedBalance;
    }

    // Updated grouped balance totals
    groupedBalance.decimalTotalBalance =
      groupedBalance.decimalTotalBalance.plus(balance.decimalBalance);
    if (!balance.isRateLoading && !groupedBalance.isRateLoading) {
      groupedBalance.totalBalanceUsd =
        (groupedBalance.totalBalanceUsd ?? 0) + (balance.balanceUsd ?? 0);
      groupedBalance.totalBalance24hChangeUsd =
        (groupedBalance.totalBalance24hChangeUsd ?? 0) +
        (balance.balance24hChangeUsd ?? 0);
    }
    groupedBalance.custody = addUniqueCustodyAddresses(
      groupedBalance.custody,
      balance,
      accountAddresses
    );
    groupedBalance.walletIds = addUniqueWalletIds(
      groupedBalance.walletIds,
      balance,
      accountAddresses
    );
    groupedBalance.sources = addUniqueSources(
      groupedBalance.sources,
      balance,
      accountAddresses
    );

    let groupedChainBalance = groupedBalance.chainAssetBalances[balance.chain];
    if (!groupedChainBalance) {
      const { walletAddress: _walletAddress, ...rest } = balance;
      groupedChainBalance = {
        ...rest,
        rawBalance: new BigNumber(0),
        decimalBalance: new BigNumber(0),
        balanceUsd: 0,
        balance24hChangeUsd: 0,
        custody: [],
        walletIds: [],
        sources: [],
      };
      groupedBalance.chainAssetBalances[balance.chain] = groupedChainBalance;
    }

    // Update grouped chain balance totals
    groupedChainBalance.rawBalance = groupedChainBalance.rawBalance.plus(
      balance.rawBalance
    );
    groupedChainBalance.decimalBalance =
      groupedChainBalance.decimalBalance.plus(balance.decimalBalance);

    if (!balance.isRateLoading && !groupedChainBalance.isRateLoading) {
      groupedChainBalance.balanceUsd =
        (groupedChainBalance.balanceUsd ?? 0) + (balance.balanceUsd ?? 0);
      groupedChainBalance.balance24hChangeUsd =
        (groupedChainBalance.balance24hChangeUsd ?? 0) +
        (balance.balance24hChangeUsd ?? 0);
    }
    groupedChainBalance.custody = addUniqueCustodyAddresses(
      groupedChainBalance.custody,
      balance,
      accountAddresses
    );
    groupedChainBalance.walletIds = addUniqueWalletIds(
      groupedChainBalance.walletIds,
      balance,
      accountAddresses
    );
    groupedChainBalance.sources = addUniqueSources(
      groupedChainBalance.sources,
      balance,
      accountAddresses
    );
  }

  return balancesByGroupId;
};
